import { AlertTriangle, ChevronRight, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useMinLoading } from "@/lib/hooks/useMinLoading";
import { cn } from "@/lib/utils";
import {
  type DetailTab,
  getDefaultDetailTab,
  getWorklistSummary,
  isMonitoredReconcileItem,
  sortReconcileItems,
  statusClass,
  statusLabel,
} from "./reconcile-helpers";
import { useBlDocuments } from "./useBlDocuments";
import { useReconcile } from "./useReconcile";
import { VerifyBlInspectorContent } from "./VerifyBlInspectorContent";

type VerifyFatturePolizzeTabProps = {
  isActive: boolean;
  onUnauthorized: (message?: string) => void;
};

type InspectorLayoutMode =
  | "mobile"
  | "desktop"
  | "desktop-compact"
  | "desktop-overlay";

const EMPTY_DOCUMENTS_STATE = {
  data: null,
  loading: false,
  errorMessage: null,
  hasLoaded: false,
};

export function VerifyFatturePolizzeTab({
  isActive,
  onUnauthorized,
}: VerifyFatturePolizzeTabProps) {
  const {
    data,
    filters,
    loading,
    errorMessage,
    hasLoaded,
    setFilters,
    refresh,
  } = useReconcile({ onUnauthorized });
  const {
    stateByBl,
    downloadingSha,
    downloadingReportSha,
    downloadErrorMessage,
    ensureDocuments,
    downloadDocument,
    downloadDocumentReport,
    clearDownloadError,
    invalidateAll,
  } = useBlDocuments({ onUnauthorized });
  const [selectedBl, setSelectedBl] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>("duplicates");
  const [layoutMode, setLayoutMode] = useState<InspectorLayoutMode>("mobile");
  const [inspectorDialogOpen, setInspectorDialogOpen] = useState(false);
  const { loading: minRefreshLoading, run: runRefresh } = useMinLoading({
    minMs: 1000,
  });
  const inspectorDialogRef = useRef<HTMLDialogElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const shouldRestoreFocusRef = useRef(true);
  const isUnmountingRef = useRef(false);

  useEffect(() => {
    if (isActive) {
      void refresh();
    }
  }, [isActive, refresh]);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 1024px)");
    const compactHeightQuery = window.matchMedia(
      "(min-height: 701px) and (max-height: 860px)",
    );
    const overlayHeightQuery = window.matchMedia("(max-height: 700px)");
    const updateLayoutMode = () => {
      if (!desktopQuery.matches) {
        setLayoutMode("mobile");
        return;
      }
      if (overlayHeightQuery.matches) {
        setLayoutMode("desktop-overlay");
        return;
      }
      if (compactHeightQuery.matches) {
        setLayoutMode("desktop-compact");
        return;
      }
      setLayoutMode("desktop");
    };

    updateLayoutMode();
    desktopQuery.addEventListener("change", updateLayoutMode);
    compactHeightQuery.addEventListener("change", updateLayoutMode);
    overlayHeightQuery.addEventListener("change", updateLayoutMode);
    return () => {
      desktopQuery.removeEventListener("change", updateLayoutMode);
      compactHeightQuery.removeEventListener("change", updateLayoutMode);
      overlayHeightQuery.removeEventListener("change", updateLayoutMode);
    };
  }, []);

  const monitoredItems = useMemo(() => {
    return sortReconcileItems(
      (data?.items ?? []).filter(isMonitoredReconcileItem),
    );
  }, [data?.items]);

  const highRiskItems = useMemo(
    () =>
      monitoredItems.filter((item) => (item.duplicate_pairs_count ?? 0) > 0),
    [monitoredItems],
  );

  const selectedItem = useMemo(() => {
    if (!selectedBl) return monitoredItems[0] ?? null;
    return (
      monitoredItems.find((item) => item.bl === selectedBl) ??
      monitoredItems[0] ??
      null
    );
  }, [monitoredItems, selectedBl]);

  const totalDuplicates = useMemo(
    () =>
      monitoredItems.reduce(
        (acc, item) => acc + (item.duplicate_pairs_count ?? 0),
        0,
      ),
    [monitoredItems],
  );

  const monitoredCount = monitoredItems.length;

  const selectedDocumentsState =
    (selectedItem ? stateByBl[selectedItem.bl] : null) ?? EMPTY_DOCUMENTS_STATE;
  const useInlineInspector =
    layoutMode === "desktop" || layoutMode === "desktop-compact";
  const isCompactInspector = layoutMode === "desktop-compact";
  const isDesktopOverlay = layoutMode === "desktop-overlay";

  useEffect(() => {
    if (!selectedItem) return;
    void ensureDocuments(selectedItem.bl);
  }, [ensureDocuments, selectedItem]);

  const finalizeInspectorDialogClose = useCallback(() => {
    const nextFocusTarget = returnFocusRef.current;
    returnFocusRef.current = null;

    if (isUnmountingRef.current) {
      shouldRestoreFocusRef.current = true;
      return;
    }

    setInspectorDialogOpen(false);

    if (shouldRestoreFocusRef.current) {
      nextFocusTarget?.focus();
    }

    shouldRestoreFocusRef.current = true;
  }, []);

  const closeInspectorDialog = useCallback(
    ({ restoreFocus = true }: { restoreFocus?: boolean } = {}) => {
      shouldRestoreFocusRef.current = restoreFocus;
      const dialog = inspectorDialogRef.current;

      if (dialog?.open) {
        dialog.close();
        return;
      }

      finalizeInspectorDialogClose();
    },
    [finalizeInspectorDialogClose],
  );

  useEffect(() => {
    const dialog = inspectorDialogRef.current;
    if (!dialog) return;

    if (
      !isActive ||
      useInlineInspector ||
      !selectedItem ||
      !inspectorDialogOpen
    ) {
      closeInspectorDialog({ restoreFocus: false });
      return;
    }

    if (!dialog.open) {
      dialog.showModal();
    }
  }, [
    closeInspectorDialog,
    inspectorDialogOpen,
    isActive,
    selectedItem,
    useInlineInspector,
  ]);

  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
      shouldRestoreFocusRef.current = false;
      returnFocusRef.current = null;
      const dialog = inspectorDialogRef.current;
      if (dialog?.open) {
        dialog.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedItem) return;
    if (selectedBl && selectedItem.bl === selectedBl) return;
    setDetailTab(getDefaultDetailTab(selectedItem));
  }, [selectedBl, selectedItem]);

  function handleSelectBl(
    bl: string,
    openInspector = false,
    nextTab?: DetailTab,
    triggerElement?: HTMLElement | null,
  ) {
    clearDownloadError();
    setSelectedBl(bl);
    const nextSelectedItem =
      monitoredItems.find((item) => item.bl === bl) ?? selectedItem;
    if (nextTab) {
      setDetailTab(nextTab);
    } else if (nextSelectedItem) {
      setDetailTab(getDefaultDetailTab(nextSelectedItem));
    }
    if (!useInlineInspector && openInspector) {
      returnFocusRef.current = triggerElement ?? null;
      setInspectorDialogOpen(true);
    }
  }

  function handleCloseInspectorDialog() {
    closeInspectorDialog();
  }

  async function handleRefresh() {
    clearDownloadError();
    invalidateAll();
    await runRefresh(refresh);
  }

  const refreshLoading = loading || minRefreshLoading;

  return (
    <section className="panel-screen">
      <header className="panel-screen__header">
        <h2 className="panel-screen__title">Verifica Fatture-Polizze</h2>
        <p className="panel-screen__description">
          Mostra i BL attesi (polizze) e i BL trovati (fatture/DDT) per
          identificare possibili doppi addebiti.
        </p>
      </header>

      <div className="panel-screen__body verify-panel">
        <section className="verify-toolbar" aria-label="Filtri verifica">
          <div className="verify-toolbar__field">
            <div className="verify-toolbar__heading">
              <label className="upload-form__label" htmlFor="verify-bl-filter">
                Filtro BL
              </label>
              <p className="verify-toolbar__copy">
                Cerca una polizza specifica oppure scorri la worklist per aprire
                il dettaglio operativo dei BL monitorati e i documenti sorgente.
              </p>
            </div>

            <div className="verify-toolbar__controls">
              <input
                id="verify-bl-filter"
                name="blFilter"
                autoComplete="off"
                spellCheck={false}
                className="upload-form__input verify-toolbar__input"
                type="text"
                placeholder="Es. 260464037"
                value={filters.bl}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, bl: event.target.value }))
                }
                disabled={loading}
              />

              <Button
                type="button"
                variant="outline"
                className="verify-toolbar__refresh"
                onClick={() => void handleRefresh()}
                disabled={refreshLoading}
              >
                <RefreshCw
                  size={14}
                  className={cn(
                    refreshLoading
                      ? "status-panel__runs-refresh-icon--spin"
                      : "",
                  )}
                  aria-hidden="true"
                />
                Aggiorna
              </Button>
            </div>
          </div>
        </section>

        <section className="verify-stats" aria-label="Riepilogo verifica">
          <div className="verify-stat">
            <span className="verify-stat__label">Totale BL</span>
            <strong className="verify-stat__value">
              {data?.total_bls ?? 0}
            </strong>
          </div>
          <div className="verify-stat">
            <span className="verify-stat__label">Allineati</span>
            <strong className="verify-stat__value">
              {data?.aligned_bls ?? 0}
            </strong>
          </div>
          <div className="verify-stat">
            <span className="verify-stat__label">BL monitorati</span>
            <strong className="verify-stat__value">{monitoredCount}</strong>
          </div>
          <div className="verify-stat verify-stat--risk">
            <span className="verify-stat__label">Possibili doppi addebiti</span>
            <strong className="verify-stat__value">{totalDuplicates}</strong>
          </div>
        </section>

        {errorMessage ? (
          <p className="upload-form__error" role="alert">
            Errore: {errorMessage}
          </p>
        ) : null}

        {highRiskItems.length > 0 ? (
          <section className="verify-priority" aria-label="Rischi prioritari">
            <h3 className="status-panel__section-title">Rischi prioritari</h3>
            <ul className="verify-priority__list">
              {highRiskItems.map((item) => (
                <li key={`risk-${item.bl}`}>
                  <button
                    type="button"
                    className={cn(
                      "verify-priority__item",
                      selectedItem?.bl === item.bl
                        ? "verify-priority__item--active"
                        : "",
                    )}
                    onClick={(event) =>
                      handleSelectBl(
                        item.bl,
                        true,
                        "duplicates",
                        event.currentTarget,
                      )
                    }
                  >
                    <span className="verify-priority__item-main">
                      <AlertTriangle size={14} aria-hidden="true" /> BL{" "}
                      {item.bl}
                    </span>
                    <span className="verify-priority__item-meta">
                      {item.duplicate_pairs_count ?? 0} possibili doppi addebiti
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section
          className={cn(
            "verify-workspace",
            isDesktopOverlay ? "verify-workspace--overlay" : "",
            isCompactInspector ? "verify-workspace--compact" : "",
          )}
          aria-label="Risultati verifica per BL"
        >
          <div
            className={cn(
              "verify-worklist",
              isDesktopOverlay ? "verify-worklist--overlay" : "",
              isCompactInspector ? "verify-worklist--compact" : "",
            )}
          >
            <div className="verify-worklist__header">
              <div>
                <span className="verify-worklist__eyebrow">Worklist</span>
                <h3 className="status-panel__section-title">BL monitorati</h3>
              </div>
              <span className="verify-worklist__meta">
                {monitoredCount} in monitoraggio
              </span>
            </div>

            {monitoredItems.length === 0 ? (
              <div className="verify-worklist__empty">
                {hasLoaded
                  ? "Nessun BL monitorato disponibile con i filtri correnti."
                  : "Caricamento risultati verifica in corso…"}
              </div>
            ) : (
              <ul className="verify-worklist__list">
                {monitoredItems.map((item) => {
                  const isSelected = selectedItem?.bl === item.bl;

                  return (
                    <li key={item.bl}>
                      <button
                        type="button"
                        className={cn(
                          "verify-worklist__item",
                          isSelected ? "verify-worklist__item--selected" : "",
                        )}
                        aria-pressed={isSelected}
                        onClick={(event) =>
                          handleSelectBl(
                            item.bl,
                            true,
                            undefined,
                            event.currentTarget,
                          )
                        }
                      >
                        <div className="verify-worklist__item-header">
                          <div className="verify-worklist__item-title">
                            <strong>{item.bl}</strong>
                            <span
                              className={cn(
                                "verify-status-badge",
                                statusClass(item.status),
                              )}
                            >
                              {statusLabel(item.status)}
                            </span>
                          </div>
                          <ChevronRight size={16} aria-hidden="true" />
                        </div>

                        <p className="verify-worklist__summary">
                          {getWorklistSummary(item)}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {useInlineInspector && selectedItem ? (
            <aside
              className="verify-inspector"
              aria-label={`Dettaglio BL ${selectedItem.bl}`}
            >
              <VerifyBlInspectorContent
                key={`${selectedItem.bl}-${layoutMode}`}
                selectedItem={selectedItem}
                detailTab={detailTab}
                layoutMode="regular"
                documentsState={selectedDocumentsState}
                downloadingSha={downloadingSha}
                downloadingReportSha={downloadingReportSha}
                downloadErrorMessage={downloadErrorMessage}
                onDetailTabChange={setDetailTab}
                onRetryDocuments={() =>
                  void ensureDocuments(selectedItem.bl, { force: true })
                }
                onDownloadDocument={downloadDocument}
                onDownloadDocumentReport={downloadDocumentReport}
              />
            </aside>
          ) : null}
        </section>

        {!useInlineInspector && selectedItem ? (
          <dialog
            ref={inspectorDialogRef}
            className={cn(
              "verify-drawer",
              isDesktopOverlay ? "verify-drawer--desktop" : "",
            )}
            onClose={finalizeInspectorDialogClose}
            onCancel={(event) => {
              event.preventDefault();
              handleCloseInspectorDialog();
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                handleCloseInspectorDialog();
              }
            }}
            onKeyDown={(event) => {
              if (
                event.target === event.currentTarget &&
                (event.key === "Enter" || event.key === " ")
              ) {
                event.preventDefault();
                handleCloseInspectorDialog();
              }
            }}
          >
            <div
              className={cn(
                "verify-drawer__surface",
                isDesktopOverlay ? "verify-drawer__surface--desktop" : "",
              )}
            >
              <div className="verify-drawer__header">
                <div>
                  <span className="verify-worklist__eyebrow">Dettaglio BL</span>
                  <h3 className="status-panel__section-title">
                    {selectedItem.bl}
                  </h3>
                </div>
                <button
                  type="button"
                  className="verify-drawer__close"
                  onClick={handleCloseInspectorDialog}
                  aria-label="Chiudi dettaglio BL"
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </div>

              <VerifyBlInspectorContent
                key={`${selectedItem.bl}-overlay`}
                selectedItem={selectedItem}
                detailTab={detailTab}
                layoutMode="regular"
                documentsState={selectedDocumentsState}
                downloadingSha={downloadingSha}
                downloadingReportSha={downloadingReportSha}
                downloadErrorMessage={downloadErrorMessage}
                onDetailTabChange={setDetailTab}
                onRetryDocuments={() =>
                  void ensureDocuments(selectedItem.bl, { force: true })
                }
                onDownloadDocument={downloadDocument}
                onDownloadDocumentReport={downloadDocumentReport}
              />
            </div>
          </dialog>
        ) : null}
      </div>
    </section>
  );
}
